import { useEffect, useMemo, useRef } from "react";
import { ArrowLeft, Search, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

/**
 * Full-screen scene picker used inside AssignmentDialog.
 * Renders as a fixed overlay above the parent Dialog so the
 * user gets a focused, one-task-per-screen experience on phones.
 *
 * Pure presentation — every piece of state lives in the parent
 * (AssignmentDialog) so closing the picker preserves selections
 * and the Save flow can stay untouched.
 */
export default function AssignmentScenePicker({
    open,
    onClose,
    scenes,
    filteredScenes,
    episodes,
    episodeFilter,
    setEpisodeFilter,
    selectedIds,
    onToggle,
    onClear,
    query,
    setQuery,
    formatSceneLabel,
    loading,
    entryMode,
}) {
    const inputRef = useRef(null);

    // Auto-focus the search input the first paint after open. Gives
    // mobile users an immediate keyboard so they can type & filter
    // without an extra tap.
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => inputRef.current?.focus(), 60);
        return () => clearTimeout(t);
    }, [open]);

    const selectedCount = selectedIds.length;

    const summary = useMemo(() => {
        if (selectedCount === 0) return null;
        const labels = selectedIds
            .map((id) => {
                const s = scenes.find((x) => x.id === id);
                return s ? formatSceneLabel(s) : null;
            })
            .filter(Boolean);
        if (labels.length === 0) return null;
        if (labels.length <= 3) return labels.join(" · ");
        return `${labels.slice(0, 3).join(" · ")} +${labels.length - 3}`;
    }, [selectedIds, scenes, formatSceneLabel, selectedCount]);

    if (!open) return null;

    return (
        <>
            {/* Desktop backdrop dimmer — hidden on mobile where the
                picker fills the full viewport already. */}
            <div
                aria-hidden="true"
                className="hidden sm:block fixed inset-0 z-[59] bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />
            <div
                data-testid="scene-picker"
                className="fixed inset-0 z-[60] bg-card text-white flex flex-col sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-lg sm:w-full sm:max-h-[88vh] sm:rounded-xl sm:border sm:border-white/10 sm:shadow-2xl"
                role="dialog"
                aria-modal="true"
                aria-label="Pick scenes"
            >
            {/* Header */}
            <div className="px-3 pt-3 pb-2 border-b border-white/10 flex items-center gap-2">
                <button
                    type="button"
                    data-testid="scene-picker-back"
                    onClick={onClose}
                    className="instant-tap h-10 w-10 inline-flex items-center justify-center rounded-md text-neutral-300 hover:text-white hover:bg-white/10"
                    aria-label="Back"
                >
                    <ArrowLeft size={20} />
                </button>
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-base font-bold leading-tight">
                        Pick scenes
                    </h2>
                    <p className="text-[11px] text-neutral-400 leading-snug truncate">
                        {selectedCount > 0
                            ? `${selectedCount} selected${summary ? ` · ${summary}` : ""}`
                            : entryMode === "onset"
                                ? "Required for On Set entries"
                                : "Optional for Fittings"}
                    </p>
                </div>
                {selectedCount > 0 ? (
                    <button
                        type="button"
                        data-testid="scene-picker-clear"
                        onClick={onClear}
                        className="instant-tap text-xs text-neutral-300 hover:text-white px-2 h-9 rounded-md hover:bg-white/10"
                    >
                        Clear
                    </button>
                ) : null}
            </div>

            {/* Search */}
            <div className="px-3 pt-3 pb-2">
                <div className="relative">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                    />
                    <Input
                        ref={inputRef}
                        data-testid="scene-picker-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search scenes (number or heading)"
                        className="pl-9 h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary text-base"
                    />
                </div>
            </div>

            {/* Episode pills — only when more than one episode exists. */}
            {episodes.length > 1 ? (
                <div
                    data-testid="scene-picker-episode-filter"
                    className="px-3 pb-2 flex gap-1.5 overflow-x-auto"
                >
                    <button
                        type="button"
                        data-testid="scene-picker-ep-all"
                        data-active={!episodeFilter ? "true" : "false"}
                        onClick={() => setEpisodeFilter(null)}
                        className={`instant-tap shrink-0 h-8 px-3 rounded-full text-[11px] uppercase tracking-[0.15em] border ${
                            !episodeFilter
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-secondary text-neutral-300 border-white/10"
                        }`}
                    >
                        All
                    </button>
                    {episodes.map((ep) => (
                        <button
                            key={ep}
                            type="button"
                            data-testid={`scene-picker-ep-${ep}`}
                            data-active={String(episodeFilter) === String(ep) ? "true" : "false"}
                            onClick={() => setEpisodeFilter(String(ep))}
                            className={`instant-tap shrink-0 h-8 px-3 rounded-full text-[11px] uppercase tracking-[0.15em] border ${
                                String(episodeFilter) === String(ep)
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-secondary text-neutral-300 border-white/10"
                            }`}
                        >
                            Ep {ep}
                        </button>
                    ))}
                </div>
            ) : null}

            {/* List */}
            <div
                data-testid="scene-picker-list"
                className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 min-h-0"
            >
                {loading ? (
                    <p className="text-xs text-neutral-500 px-1 py-2">Loading scenes…</p>
                ) : filteredScenes.length === 0 ? (
                    <p
                        data-testid="scene-picker-empty"
                        className="text-xs text-neutral-500 px-1 py-2"
                    >
                        {query
                            ? `No scenes match "${query}".`
                            : episodeFilter
                                ? `No confirmed scenes in Ep ${episodeFilter} yet.`
                                : "No confirmed scenes yet."}
                    </p>
                ) : (
                    filteredScenes.map((s) => {
                        const isSelected = selectedIds.includes(s.id);
                        return (
                            <button
                                key={s.id}
                                type="button"
                                data-testid={`scene-picker-row-${s.id}`}
                                data-active={isSelected ? "true" : "false"}
                                aria-pressed={isSelected}
                                onClick={() => onToggle(s.id)}
                                className={`instant-tap w-full min-h-[52px] text-left flex items-center gap-3 px-3 py-2 rounded-lg text-sm border ${
                                    isSelected
                                        ? "bg-primary/15 text-white border-primary"
                                        : "bg-secondary text-neutral-200 border-white/10 hover:bg-white/10"
                                }`}
                            >
                                <span className="mono text-xs min-w-[2.75rem] px-1 text-center shrink-0 opacity-80">
                                    {formatSceneLabel(s)}
                                </span>
                                <span className="flex-1 truncate">{s.heading || "—"}</span>
                                <span
                                    className={`h-5 w-5 rounded-md border inline-flex items-center justify-center shrink-0 ${
                                        isSelected
                                            ? "bg-primary border-primary text-primary-foreground"
                                            : "border-white/20"
                                    }`}
                                    aria-hidden="true"
                                >
                                    {isSelected ? <Check size={12} strokeWidth={3} /> : null}
                                </span>
                            </button>
                        );
                    })
                )}
            </div>

            {/* Footer */}
            <div className="px-3 py-3 border-t border-white/10 bg-card">
                <Button
                    type="button"
                    data-testid="scene-picker-done"
                    onClick={onClose}
                    className="w-full h-11 font-semibold tracking-wide"
                >
                    {selectedCount > 0
                        ? `Done · ${selectedCount} selected`
                        : "Done"}
                </Button>
            </div>
            </div>
        </>
    );
}
